import { type ReactNode, useState } from "react";

type Props = {
  titulo: string;
  /** Qué se ve cuando está plegada: el estado actual, para no tener que abrirla. */
  resumen?: ReactNode;
  children: ReactNode;
  abiertoInicial?: boolean;
  /** Si viene, recuerda abierto/plegado entre sesiones (localStorage). */
  recordarComo?: string;
};

/**
 * Sección plegable del compositor de correo.
 *
 * El modal de enviar correo acumuló bloques que solo se tocan de vez en
 * cuando (opciones, bitácora de consulta, el asistente de IA) y empujaban el
 * editor fuera de la pantalla. Cada uno se pliega y —si se le da
 * `recordarComo`— se queda como lo dejaste: la IA se usa una vez y estorba el
 * resto del rato (pedido de Joe, 2026-08-11).
 *
 * La cabecera es una fila delgada a propósito: un `card` con padding pesaba
 * más que su contenido.
 */
export default function SeccionPlegable({
  titulo,
  resumen,
  children,
  abiertoInicial = true,
  recordarComo,
}: Props) {
  const [abierto, setAbierto] = useState(() => {
    if (!recordarComo) return abiertoInicial;
    const guardado = localStorage.getItem(`plegable:${recordarComo}`);
    return guardado === null ? abiertoInicial : guardado === "1";
  });

  function alternar() {
    setAbierto((v) => {
      if (recordarComo) localStorage.setItem(`plegable:${recordarComo}`, v ? "0" : "1");
      return !v;
    });
  }

  return (
    <div className="rounded border border-ink-700 bg-ink-950/40 overflow-hidden">
      <button
        type="button"
        onClick={alternar}
        aria-expanded={abierto}
        className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-ink-800 text-left"
      >
        <span className="text-[10px] text-zinc-500 w-3 flex-shrink-0">
          {abierto ? "▼" : "▶"}
        </span>
        <span className="text-xs uppercase tracking-wide text-zinc-400 flex-shrink-0">
          {titulo}
        </span>
        {!abierto && resumen && (
          <span className="text-[11px] text-zinc-500 truncate">· {resumen}</span>
        )}
        <span className="ml-auto text-[10px] text-zinc-600 flex-shrink-0">
          {abierto ? "ocultar" : "mostrar"}
        </span>
      </button>
      {abierto && <div className="px-2 pb-2 pt-1 space-y-2">{children}</div>}
    </div>
  );
}
